import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  format,
  addDays,
  differenceInDays,
  startOfDay,
  startOfMonth,
  endOfMonth,
  subMonths,
  parseISO,
  getWeek,
  isWeekend as isDateWeekend,
  isSameDay,
  isBefore,
  isAfter,
} from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarRange,
  CalendarDays,
  CalendarClock,
  Package,
  Truck,
  ArrowRightLeft,
  Filter,
} from 'lucide-react';
import { useClientsDropdown } from '@/api/hooks/useClients';
import {
  useRequisitions,
  type RequisitionStatus,
} from '@/api/hooks/useRequisitions';
import { useAllTransfers } from '@/api/hooks/useClientHub';
import type { Client } from '@/api/hooks/useClients';

// ============================================================================
// Types
// ============================================================================

interface DeliveryBar {
  id: number;
  folio: string;
  clientId: number;
  clientName: string;
  locationName: string;
  startDate: Date;
  endDate: Date;
  requestedQuantity: number;
  suppliedQuantity: number;
  progress: number;
  status: RequisitionStatus;
}

interface TransferBar {
  id: number;
  folio: string;
  clientId: number;
  clientName: string;
  locationName: string;
  date: Date;
  amount: number;
}

type DisplayMode = 'both' | 'deliveries' | 'transfers';

interface ClientRow {
  client: Client;
  bars: DeliveryBar[];
  transferBars: TransferBar[];
  /** Window-scoped. NOT a balance — see `lifetimeBalance`. */
  totalDelivered: number;
  /** Window-scoped. NOT a balance — see `lifetimeBalance`. */
  totalTransferred: number;
  /**
   * Lifetime pallets still at the client, served by the backend
   * (delivered − transferred − recovered, all-time). This is the only
   * figure that may be labelled "saldo": subtracting two window-scoped
   * totals produces a period delta that goes negative whenever a client
   * returns pallets delivered before the window opened.
   */
  lifetimeBalance: number;
}

interface MonthSpan {
  label: string;
  span: number;
}

// ============================================================================
// Constants
// ============================================================================

const DAY_WIDTH_WEEK = 40;
const DAY_WIDTH_MONTH = 14;
const HEADER_HEIGHT = 52;
const CLIENT_PANEL_WIDTH = 240;
const SINGLE_CLIENT_ROW_HEIGHT = 52;
const SINGLE_CLIENT_LABEL_WIDTH = 160;

type ViewMode = 'week' | 'month';

// ============================================================================
// Status color configuration
// ============================================================================

const STATUS_BAR_STYLES: Record<
  Exclude<RequisitionStatus, 'Rejected'>,
  { bg: string; border: string; text: string }
> = {
  Requested: {
    bg: 'bg-amber-400',
    border: 'border-amber-600',
    text: 'text-amber-950',
  },
  Approved: {
    bg: 'bg-blue-500',
    border: 'border-blue-600',
    text: 'text-white',
  },
  InProgress: {
    bg: 'bg-primary/80',
    border: 'border-primary',
    text: 'text-primary-foreground',
  },
  Completed: {
    bg: 'bg-emerald-500',
    border: 'border-emerald-600',
    text: 'text-white',
  },
};

const STATUS_LEGEND_COLORS: Record<
  Exclude<RequisitionStatus, 'Rejected'>,
  string
> = {
  Requested: 'bg-amber-400',
  Approved: 'bg-blue-500',
  InProgress: 'bg-primary/80',
  Completed: 'bg-emerald-500',
};

// ============================================================================
// Helpers
// ============================================================================

/**
 * Generate an array of dates between start and end (inclusive).
 */
function generateDateRange(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  let current = startOfDay(start);
  const endDay = startOfDay(end);
  while (!isAfter(current, endDay)) {
    dates.push(current);
    current = addDays(current, 1);
  }
  return dates;
}

/**
 * Check if two date ranges overlap.
 */
function rangesOverlap(
  aStart: Date,
  aEnd: Date,
  bStart: Date,
  bEnd: Date
): boolean {
  return !isAfter(aStart, bEnd) && !isBefore(aEnd, bStart);
}

/**
 * Get the dynamic row height based on display mode.
 *
 * Sized for the client panel's stat chips, which wrap inside
 * CLIENT_PANEL_WIDTH: "both" shows two period figures plus the lifetime
 * saldo, the other modes show one period figure plus the saldo. The
 * "(período)" qualifiers make these labels long enough to wrap, hence the
 * extra room versus the pre-relabel 80/52.
 */
function getRowHeight(mode: DisplayMode): number {
  return mode === 'both' ? 92 : 64;
}

// ============================================================================
// Main Component
// ============================================================================

interface DeliverySchedulerProps {
  /** When provided, filter the Gantt chart to a single client */
  clientId?: number;
}

export function DeliveryScheduler({ clientId }: DeliverySchedulerProps = {}) {
  const { t, i18n } = useTranslation('dashboard');
  const locale = i18n.language === 'es' ? es : enUS;

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [displayMode, setDisplayMode] = useState<DisplayMode>('both');
  const [selectedClientIds, setSelectedClientIds] = useState<Set<number> | null>(null); // null = all selected
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>(
    () => {
      const now = new Date();
      return {
        start: startOfMonth(subMonths(now, 3)),
        end: endOfMonth(now),
      };
    }
  );

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  // Server-side window bounds, padded by a day on each side and formatted as
  // LOCAL wall-clock (not .toISOString()). Both date columns are `timestamp
  // without time zone`, so wall-clock is the correct comparison; the padding
  // means any residual timezone skew can only ever widen the fetched set.
  // The precise trimming still happens client-side against `dateRange`, so
  // this is purely a prefilter that replaces the old "200 newest rows across
  // all clients" truncation.
  const { fetchFrom, fetchTo } = useMemo(() => {
    const fmt = (d: Date) => format(d, "yyyy-MM-dd'T'HH:mm:ss");
    return {
      fetchFrom: fmt(startOfDay(addDays(dateRange.start, -1))),
      fetchTo: fmt(addDays(startOfDay(dateRange.end), 2)),
    };
  }, [dateRange]);

  const { data: clients, isLoading: clientsLoading } = useClientsDropdown();
  const { data: reqData, isLoading: reqsLoading } = useRequisitions({
    pageSize: 200,
    clientId,
    fromDate: fetchFrom,
    toDate: fetchTo,
  });
  const { data: transfersData, isLoading: transfersLoading } =
    useAllTransfers(clientId, fetchFrom, fetchTo);

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const singleClient = clientId !== undefined;
  // True when the server had more requisitions in this window than the
  // pageSize cap returned, so the period totals below are partial.
  const requisitionsTruncated =
    !!reqData && reqData.totalCount > reqData.items.length;
  const rowHeight = getRowHeight(displayMode);
  const dayWidth = viewMode === 'week' ? DAY_WIDTH_WEEK : DAY_WIDTH_MONTH;
  const allDays = useMemo(
    () => generateDateRange(dateRange.start, dateRange.end),
    [dateRange]
  );
  const totalTimelineWidth = allDays.length * dayWidth;
  const today = useMemo(() => startOfDay(new Date()), []);

  // ---------------------------------------------------------------------------
  // Data transformation
  // ---------------------------------------------------------------------------

  const clientRows = useMemo<ClientRow[]>(() => {
    if (!clients) return [];

    const clientMap = new Map<number, Client>();
    for (const c of clients) {
      clientMap.set(c.id, c);
    }

    // --- Process delivery bars from requisitions ---
    const deliveryGrouped = new Map<number, DeliveryBar[]>();

    if (reqData?.items) {
      for (const req of reqData.items) {
        if ((req.status as string) === 'Rejected') continue;

        const startDate = parseISO(req.requestedDate);
        let endDate: Date;
        if (req.completedDate) {
          endDate = parseISO(req.completedDate);
        } else if (req.status === 'InProgress') {
          endDate = new Date();
        } else {
          endDate = addDays(startDate, 7);
        }

        const progress =
          req.requestedQuantity > 0
            ? Math.round((req.suppliedQuantity / req.requestedQuantity) * 100)
            : 0;

        const bar: DeliveryBar = {
          id: req.id,
          folio: req.folio,
          clientId: req.clientId,
          clientName: req.clientName,
          locationName: req.locationName,
          startDate: startOfDay(startDate),
          endDate: startOfDay(endDate),
          requestedQuantity: req.requestedQuantity,
          suppliedQuantity: req.suppliedQuantity,
          progress,
          status: req.status,
        };

        // Only keep bars that overlap with the visible date range
        if (
          rangesOverlap(
            bar.startDate,
            bar.endDate,
            dateRange.start,
            dateRange.end
          )
        ) {
          const existing = deliveryGrouped.get(req.clientId) ?? [];
          existing.push(bar);
          deliveryGrouped.set(req.clientId, existing);
        }
      }
    }

    // --- Process transfer bars ---
    const transferGrouped = new Map<number, TransferBar[]>();

    if (transfersData) {
      for (const transfer of transfersData) {
        const transferDate = startOfDay(parseISO(transfer.date));

        // Filter: transfer date must fall within the visible date range
        if (
          isBefore(transferDate, dateRange.start) ||
          isAfter(transferDate, dateRange.end)
        ) {
          continue;
        }

        const tBar: TransferBar = {
          id: transfer.id,
          folio: transfer.folio,
          clientId: transfer.clientId,
          clientName: transfer.clientName,
          locationName: transfer.locationName,
          date: transferDate,
          amount: transfer.amount,
        };

        const existing = transferGrouped.get(transfer.clientId) ?? [];
        existing.push(tBar);
        transferGrouped.set(transfer.clientId, existing);
      }
    }

    // --- Merge into client rows ---
    // Collect all unique client IDs from both deliveries and transfers
    const allClientIds = new Set<number>([
      ...deliveryGrouped.keys(),
      ...transferGrouped.keys(),
    ]);

    const rows: ClientRow[] = [];
    for (const clientId of allClientIds) {
      const client = clientMap.get(clientId);
      if (!client) continue;

      const bars = deliveryGrouped.get(clientId) ?? [];
      const transferBars = transferGrouped.get(clientId) ?? [];

      const totalDelivered = bars.reduce(
        (sum, b) => sum + b.suppliedQuantity,
        0
      );
      const totalTransferred = transferBars.reduce(
        (sum, tb) => sum + tb.amount,
        0
      );

      rows.push({
        client,
        bars,
        transferBars,
        totalDelivered,
        totalTransferred,
        // Already hydrated by useClientsDropdown → transformClient, so this
        // costs zero extra network calls.
        lifetimeBalance: client.balance ?? 0,
      });
    }

    // Sort by lifetime saldo descending (most outstanding first). Sorting by
    // the period delta made row order jump around on every prev/next click;
    // the lifetime figure is stable across navigation. Name breaks ties so
    // the order is deterministic.
    rows.sort(
      (a, b) =>
        b.lifetimeBalance - a.lifetimeBalance ||
        (a.client.name ?? '').localeCompare(b.client.name ?? '')
    );

    return rows;
  }, [reqData, clients, transfersData, dateRange]);

  // Filter client rows based on selected client IDs (null = show all)
  const filteredClientRows = useMemo(() => {
    if (!selectedClientIds) return clientRows;
    return clientRows.filter((r) => selectedClientIds.has(r.client.id));
  }, [clientRows, selectedClientIds]);

  // Client filter toggle handler
  const toggleClientFilter = useCallback((id: number) => {
    setSelectedClientIds((prev) => {
      if (!prev) {
        // Currently all selected → deselect this one
        const allIds = new Set(clientRows.map((r) => r.client.id));
        allIds.delete(id);
        return allIds;
      }
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      // If all are selected again, reset to null
      if (next.size === clientRows.length) return null;
      return next;
    });
  }, [clientRows]);

  const selectAllClients = useCallback(() => setSelectedClientIds(null), []);
  const deselectAllClients = useCallback(() => setSelectedClientIds(new Set()), []);

  // Summary stats
  const totalDeliveries = useMemo(
    () => filteredClientRows.reduce((sum, row) => sum + row.bars.length, 0),
    [filteredClientRows]
  );

  const totalTransfers = useMemo(
    () => filteredClientRows.reduce((sum, row) => sum + row.transferBars.length, 0),
    [filteredClientRows]
  );

  // In single-client mode, flatten all bars into two groups: deliveries and transfers
  const singleClientData = useMemo(() => {
    if (!singleClient) return null;
    return {
      deliveryBars: filteredClientRows.flatMap((r) => r.bars),
      transferBars: filteredClientRows.flatMap((r) => r.transferBars),
    };
  }, [singleClient, filteredClientRows]);

  // ---------------------------------------------------------------------------
  // Month spans for header
  // ---------------------------------------------------------------------------

  const monthSpans = useMemo<MonthSpan[]>(() => {
    const spans: MonthSpan[] = [];
    let currentLabel = '';
    for (const day of allDays) {
      const label = format(day, 'MMMM yyyy', { locale });
      if (label !== currentLabel) {
        spans.push({ label, span: 1 });
        currentLabel = label;
      } else {
        spans[spans.length - 1].span++;
      }
    }
    return spans;
  }, [allDays, locale]);

  // ---------------------------------------------------------------------------
  // Today marker position
  // ---------------------------------------------------------------------------

  const todayOffset = useMemo(() => {
    if (isBefore(today, dateRange.start) || isAfter(today, dateRange.end)) {
      return null;
    }
    const days = differenceInDays(today, startOfDay(dateRange.start));
    return days * dayWidth + dayWidth / 2;
  }, [today, dateRange, dayWidth]);

  // ---------------------------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------------------------

  function handlePrevious() {
    const shift = viewMode === 'week' ? 7 : 30;
    setDateRange((prev) => ({
      start: addDays(prev.start, -shift),
      end: addDays(prev.end, -shift),
    }));
  }

  function handleNext() {
    const shift = viewMode === 'week' ? 7 : 30;
    setDateRange((prev) => ({
      start: addDays(prev.start, shift),
      end: addDays(prev.end, shift),
    }));
  }

  function handleToday() {
    const now = new Date();
    if (viewMode === 'week') {
      const start = addDays(startOfDay(now), -7);
      const end = addDays(startOfDay(now), 14);
      setDateRange({ start, end });
    } else {
      setDateRange({
        start: startOfMonth(subMonths(now, 1)),
        end: endOfMonth(addDays(now, 30)),
      });
    }
  }

  // ---------------------------------------------------------------------------
  // Bar position calculators
  // ---------------------------------------------------------------------------

  function getDeliveryBarStyle(bar: DeliveryBar): React.CSSProperties {
    const rangeStart = startOfDay(dateRange.start);
    const clampedStart = isBefore(bar.startDate, rangeStart)
      ? rangeStart
      : bar.startDate;
    const clampedEnd = isAfter(bar.endDate, dateRange.end)
      ? dateRange.end
      : bar.endDate;

    const leftDays = differenceInDays(clampedStart, rangeStart);
    const durationDays = differenceInDays(clampedEnd, clampedStart) + 1;

    if (!singleClient && displayMode === 'both') {
      return {
        position: 'absolute' as const,
        left: leftDays * dayWidth,
        width: Math.max(durationDays * dayWidth - 2, 24),
        top: 6,
        height: 32,
      };
    }

    return {
      position: 'absolute' as const,
      left: leftDays * dayWidth,
      width: Math.max(durationDays * dayWidth - 2, 24),
      top: 6,
      height: 40,
    };
  }

  function getTransferBarStyle(bar: TransferBar): React.CSSProperties {
    const rangeStart = startOfDay(dateRange.start);
    const leftDays = differenceInDays(bar.date, rangeStart);
    const barWidth = Math.max(dayWidth, 28);

    if (!singleClient && displayMode === 'both') {
      return {
        position: 'absolute' as const,
        left: leftDays * dayWidth,
        width: barWidth,
        top: 44,
        height: 32,
      };
    }

    // Single-lane mode (singleClient OR transfers-only)
    return {
      position: 'absolute' as const,
      left: leftDays * dayWidth,
      width: barWidth,
      top: 6,
      height: 40,
    };
  }

  // ---------------------------------------------------------------------------
  // Visibility helpers
  // ---------------------------------------------------------------------------

  const showDeliveries = displayMode === 'both' || displayMode === 'deliveries';
  const showTransfers = displayMode === 'both' || displayMode === 'transfers';

  // ---------------------------------------------------------------------------
  // Loading state
  // ---------------------------------------------------------------------------

  // First load only. Stepping the window changes both query keys, and with
  // `keepPreviousData` the previous window stays rendered while the next one
  // loads — so gating on `isLoading` alone would blank the entire card
  // (toolbar included) on every Previous/Next click and reset the timeline's
  // horizontal scroll.
  const firstLoad =
    (clientsLoading && !clients) ||
    (reqsLoading && !reqData) ||
    (transfersLoading && !transfersData);

  if (firstLoad) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-2">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
              <p className="text-sm text-muted-foreground">
                {t('common:loading', 'Loading...')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // Empty state
  // ---------------------------------------------------------------------------

  if (clientRows.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t('scheduler.title', 'Delivery Schedule')}</CardTitle>
          <CardDescription>
            {t(
              'scheduler.description',
              'Timeline of pallet deliveries by client'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-2 text-muted-foreground">
              <CalendarRange className="h-12 w-12" />
              <p>{t('scheduler.noDeliveries', 'No deliveries found')}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const singleClientRowCount = (showDeliveries ? 1 : 0) + (showTransfers ? 1 : 0);
  const totalContentHeight = singleClient
    ? singleClientRowCount * SINGLE_CLIENT_ROW_HEIGHT
    : filteredClientRows.length * rowHeight;
  const isFiltered = selectedClientIds !== null;

  return (
    <Card>
      {/* Card Header with title + summary badges */}
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <CardTitle>
              {t('scheduler.title', 'Delivery Schedule')}
            </CardTitle>
            <CardDescription>
              {t(
                'scheduler.description',
                'Timeline of pallet deliveries by client'
              )}
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className="gap-1">
              <Package className="h-3 w-3" />
              {t('scheduler.totalDeliveries', 'Total deliveries')}:{' '}
              {totalDeliveries}
            </Badge>
            {showTransfers && (
              <Badge variant="outline" className="gap-1 border-cyan-300 text-cyan-700 dark:text-cyan-400">
                <ArrowRightLeft className="h-3 w-3" />
                {t('scheduler.totalTransfers', 'Total transfers')}:{' '}
                {totalTransfers}
              </Badge>
            )}
            {/* The requisition endpoint clamps pageSize at 200. Within a date
                window that is normally ample, but a busy tenant can still
                exceed it — in which case the period figures are computed over
                a truncated set. Say so rather than showing a silently wrong
                number. The lifetime saldo is unaffected: it comes from the
                backend, not from these rows. */}
            {requisitionsTruncated && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge
                    variant="outline"
                    className="gap-1 border-amber-400 text-amber-700 dark:text-amber-400"
                  >
                    ⚠ {t('scheduler.truncated', 'Partial period totals')}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {t('scheduler.truncatedHint', {
                    defaultValue:
                      'Showing {{shown}} of {{total}} requisitions in this window. Narrow the date range for exact period totals; the saldo is unaffected.',
                    shown: reqData?.items.length ?? 0,
                    total: reqData?.totalCount ?? 0,
                  })}
                </TooltipContent>
              </Tooltip>
            )}
            {!singleClient && (
              <Popover>
                <PopoverTrigger asChild>
                  <button className="inline-flex items-center gap-1 rounded-md border px-2.5 py-0.5 text-xs font-semibold transition-colors cursor-pointer hover:bg-accent bg-secondary text-secondary-foreground">
                    {isFiltered && <Filter className="h-3 w-3 text-primary" />}
                    {filteredClientRows.length}/{clientRows.length}{' '}
                    {t('scheduler.client', 'Clients')}
                    <ChevronDown className="h-3 w-3 opacity-50" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0" align="end">
                  <div className="flex items-center justify-between px-3 py-2 border-b">
                    <span className="text-xs font-medium">{t('scheduler.filterClients', 'Filter clients')}</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5" onClick={selectAllClients}>
                        {t('scheduler.all', 'All')}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] px-1.5" onClick={deselectAllClients}>
                        {t('scheduler.none', 'None')}
                      </Button>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto p-1">
                    {clientRows.map(({ client }) => {
                      const checked = !selectedClientIds || selectedClientIds.has(client.id);
                      return (
                        <label
                          key={client.id}
                          className="flex items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleClientFilter(client.id)}
                          />
                          <span className="truncate">{client.name}</span>
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {/* Toolbar */}
        <div className="flex items-center justify-between px-4 py-2 border-y bg-muted/30 flex-wrap gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={handlePrevious}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={handleToday}>
              {t('scheduler.today', 'Today')}
            </Button>
            <Button variant="outline" size="sm" onClick={handleNext}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>

          <div className="text-sm text-muted-foreground hidden sm:block">
            {format(dateRange.start, 'd MMM yyyy', { locale })} &mdash;{' '}
            {format(dateRange.end, 'd MMM yyyy', { locale })}
          </div>

          {/* Display Mode Toggle */}
          <div className="flex items-center gap-1 border rounded-lg p-0.5">
            <Button
              variant={displayMode === 'deliveries' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDisplayMode('deliveries')}
              className="gap-1 h-7 text-xs"
            >
              <Truck className="h-3 w-3" />
              <span className="hidden sm:inline">
                {t('scheduler.showDeliveries', 'Deliveries')}
              </span>
            </Button>
            <Button
              variant={displayMode === 'transfers' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDisplayMode('transfers')}
              className="gap-1 h-7 text-xs"
            >
              <ArrowRightLeft className="h-3 w-3" />
              <span className="hidden sm:inline">
                {t('scheduler.showTransfers', 'Transfers')}
              </span>
            </Button>
            <Button
              variant={displayMode === 'both' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setDisplayMode('both')}
              className="gap-1 h-7 text-xs"
            >
              <span className="hidden sm:inline">
                {t('scheduler.showBoth', 'Both')}
              </span>
            </Button>
          </div>

          <div className="flex items-center gap-1">
            <Button
              variant={viewMode === 'week' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('week')}
              className="gap-1"
            >
              <CalendarDays className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">
                {t('scheduler.weekView', 'Week')}
              </span>
            </Button>
            <Button
              variant={viewMode === 'month' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setViewMode('month')}
              className="gap-1"
            >
              <CalendarClock className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">
                {t('scheduler.monthView', 'Month')}
              </span>
            </Button>
          </div>
        </div>

        {/* Main Gantt Area -- CSS Grid: fixed client column + scrollable timeline */}
        <div
          className="grid overflow-hidden"
          style={{
            gridTemplateColumns: singleClient
              ? `${SINGLE_CLIENT_LABEL_WIDTH}px minmax(0, 1fr)`
              : `${CLIENT_PANEL_WIDTH}px minmax(0, 1fr)`,
          }}
        >
          {/* Column 1: Left Panel */}
          {singleClient ? (
            /* Single-client mode: show type labels (Entregas / Transferencias) */
            <div className="border-r bg-background">
              <div
                className="flex items-center px-3 border-b bg-muted/50 font-medium text-xs uppercase tracking-wider text-muted-foreground"
                style={{ height: HEADER_HEIGHT }}
              >
                {t('scheduler.type', 'Type')}
              </div>
              {showDeliveries && (
                <div
                  className="flex items-center gap-2 px-3 border-b"
                  style={{ height: SINGLE_CLIENT_ROW_HEIGHT }}
                >
                  <Truck className="h-4 w-4 text-primary shrink-0" />
                  <span className="text-sm font-medium truncate">
                    {t('scheduler.showDeliveries', 'Deliveries')}
                  </span>
                </div>
              )}
              {showTransfers && (
                <div
                  className="flex items-center gap-2 px-3 border-b"
                  style={{ height: SINGLE_CLIENT_ROW_HEIGHT }}
                >
                  <ArrowRightLeft className="h-4 w-4 text-cyan-600 dark:text-cyan-400 shrink-0" />
                  <span className="text-sm font-medium truncate">
                    {t('scheduler.showTransfers', 'Transfers')}
                  </span>
                </div>
              )}
            </div>
          ) : (
            /* Multi-client mode: show client names + stats */
            <div className="border-r bg-background">
              {/* Client column header */}
              <div
                className="flex items-center px-3 border-b bg-muted/50 font-medium text-xs uppercase tracking-wider text-muted-foreground"
                style={{ height: HEADER_HEIGHT }}
              >
                {t('scheduler.client', 'Client')}
              </div>
              {/* Client rows */}
              {filteredClientRows.map(({ client, totalDelivered, totalTransferred, lifetimeBalance }) => (
                <div
                  key={client.id}
                  className="flex items-center px-3 border-b hover:bg-muted/30 transition-colors gap-2"
                  style={{ height: rowHeight }}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {client.name}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
                      {/* The two window-scoped figures say "(período)" on their
                          face — they are period totals, not a balance. */}
                      {showDeliveries && (
                        <span>
                          {t('scheduler.deliveredPeriod', 'delivered (period)')}:{' '}
                          {totalDelivered}
                        </span>
                      )}
                      {showTransfers && totalTransferred > 0 && (
                        <span className="text-cyan-600 dark:text-cyan-400 font-medium">
                          {t('scheduler.transferredPeriod', 'transferred (period)')}:{' '}
                          {totalTransferred}
                        </span>
                      )}
                      {/* Lifetime figure from the backend — matches the Clientes
                          list and the client hub for the same client. Not gated
                          by displayMode: it is legitimately independent of which
                          bars are on screen. */}
                      <span className="text-amber-600 font-medium">
                        {t('scheduler.balanceLifetime', 'Balance')}:{' '}
                        {lifetimeBalance}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Column 2: Scrollable Timeline */}
          <div className="overflow-x-auto min-w-0">
            <div
              style={{
                width: totalTimelineWidth,
                minHeight: totalContentHeight + HEADER_HEIGHT,
              }}
            >
              {/* Timeline Header */}
              <div
                className="border-b bg-background"
                style={{ height: HEADER_HEIGHT }}
              >
                {/* Month labels row */}
                <div
                  className="flex border-b"
                  style={{ height: HEADER_HEIGHT / 2 }}
                >
                  {monthSpans.map((month, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-center border-r text-xs font-medium bg-muted/30 truncate px-1"
                      style={{ width: month.span * dayWidth }}
                    >
                      {month.span * dayWidth > 60 ? month.label : ''}
                    </div>
                  ))}
                </div>
                {/* Day/Week numbers row */}
                <div className="flex" style={{ height: HEADER_HEIGHT / 2 }}>
                  {allDays.map((day, i) => {
                    const weekend = isDateWeekend(day);
                    const isToday = isSameDay(day, today);

                    if (viewMode === 'week') {
                      return (
                        <div
                          key={i}
                          className={cn(
                            'flex items-center justify-center border-r text-[10px]',
                            weekend && 'bg-muted/50 text-muted-foreground',
                            isToday && 'bg-primary/10 font-bold text-primary'
                          )}
                          style={{ width: dayWidth }}
                        >
                          {format(day, 'd', { locale })}
                        </div>
                      );
                    }

                    const isMonday = day.getDay() === 1;
                    return (
                      <div
                        key={i}
                        className={cn(
                          'flex items-center justify-center border-r text-[9px]',
                          weekend && 'bg-muted/50',
                          isToday && 'bg-primary/10 font-bold text-primary'
                        )}
                        style={{ width: dayWidth }}
                      >
                        {isMonday ? `W${getWeek(day, { locale })}` : ''}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Grid rows + bars */}
              <div className="relative">
                {/* Background columns (weekend shading) */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{ height: totalContentHeight }}
                >
                  {allDays.map((day, i) => {
                    const weekend = isDateWeekend(day);
                    const isToday = isSameDay(day, today);
                    if (!weekend && !isToday) return null;
                    return (
                      <div
                        key={i}
                        className={cn(
                          'absolute top-0',
                          weekend && 'bg-muted/30',
                          isToday && 'bg-primary/5'
                        )}
                        style={{
                          left: i * dayWidth,
                          width: dayWidth,
                          height: totalContentHeight,
                        }}
                      />
                    );
                  })}
                </div>

                {/* Today marker */}
                {todayOffset !== null && (
                  <div
                    className="absolute z-20 pointer-events-none"
                    style={{
                      left: todayOffset,
                      top: 0,
                      height: totalContentHeight,
                    }}
                  >
                    <div className="absolute -top-1 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-red-500" />
                    <div
                      className="absolute top-0 -translate-x-px w-0.5 bg-red-500"
                      style={{ height: totalContentHeight }}
                    />
                  </div>
                )}

                {/* Timeline row lanes */}
                {singleClient && singleClientData ? (
                  /* Single-client mode: 2 rows (Entregas + Transferencias) */
                  <>
                    {showDeliveries && (
                      <div
                        className="relative border-b"
                        style={{ height: SINGLE_CLIENT_ROW_HEIGHT }}
                      >
                        {singleClientData.deliveryBars.map((bar) => {
                          const style = getDeliveryBarStyle(bar);
                          const barWidth = style.width as number;
                          const statusStyle =
                            STATUS_BAR_STYLES[
                              bar.status as Exclude<RequisitionStatus, 'Rejected'>
                            ] ?? STATUS_BAR_STYLES.Requested;

                          return (
                            <TooltipProvider key={bar.id} delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={cn(
                                      'absolute cursor-pointer rounded-sm border shadow-sm transition-all hover:shadow-md hover:z-10 hover:brightness-110',
                                      'flex items-center justify-center text-[10px] font-medium truncate',
                                      statusStyle.bg,
                                      statusStyle.border,
                                      statusStyle.text
                                    )}
                                    style={style}
                                  >
                                    {barWidth > 40 && (
                                      <span className="truncate px-1">
                                        {bar.suppliedQuantity > 0
                                          ? bar.suppliedQuantity
                                          : bar.requestedQuantity}
                                      </span>
                                    )}
                                    {bar.status === 'InProgress' &&
                                      bar.progress > 0 &&
                                      bar.progress < 100 && (
                                        <div
                                          className="absolute bottom-0 left-0 h-1 bg-white/40 rounded-b-sm"
                                          style={{ width: `${bar.progress}%` }}
                                        />
                                      )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-xs bg-popover text-popover-foreground border shadow-md p-3"
                                >
                                  <div className="space-y-1.5">
                                    <p className="font-semibold text-sm">{bar.folio}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {bar.clientName}
                                      {bar.locationName ? ` - ${bar.locationName}` : ''}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {format(bar.startDate, 'd MMM yyyy', { locale })}{' '}
                                      &mdash;{' '}
                                      {format(bar.endDate, 'd MMM yyyy', { locale })}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs">
                                      <span>
                                        {t('scheduler.requested', 'Requested')}:{' '}
                                        <strong>{bar.requestedQuantity}</strong>
                                      </span>
                                      <span>
                                        {t('scheduler.supplied', 'Supplied')}:{' '}
                                        <strong>{bar.suppliedQuantity}</strong>
                                      </span>
                                    </div>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        'text-[10px]',
                                        statusStyle.bg,
                                        statusStyle.text,
                                        'border-transparent'
                                      )}
                                    >
                                      {t(`scheduler.status.${bar.status}`, bar.status)}
                                    </Badge>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}
                      </div>
                    )}
                    {showTransfers && (
                      <div
                        className="relative border-b"
                        style={{ height: SINGLE_CLIENT_ROW_HEIGHT }}
                      >
                        {singleClientData.transferBars.map((tBar) => {
                          const style = getTransferBarStyle(tBar);
                          const barWidth = style.width as number;

                          return (
                            <TooltipProvider key={`t-${tBar.id}`} delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={cn(
                                      'absolute cursor-pointer rounded-sm border shadow-sm transition-all hover:shadow-md hover:z-10 hover:brightness-110',
                                      'flex items-center justify-center text-[10px] font-medium truncate',
                                      'bg-cyan-500 border-cyan-600 text-white'
                                    )}
                                    style={style}
                                  >
                                    {barWidth > 28 && (
                                      <span className="truncate px-1">{tBar.amount}</span>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-xs bg-popover text-popover-foreground border shadow-md p-3"
                                >
                                  <div className="space-y-1.5">
                                    <p className="font-semibold text-sm">{tBar.folio}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {tBar.clientName}
                                      {tBar.locationName ? ` - ${tBar.locationName}` : ''}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {format(tBar.date, 'd MMM yyyy', { locale })}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs">
                                      <span>
                                        {t('scheduler.amount', 'Amount')}:{' '}
                                        <strong>{tBar.amount}</strong>
                                      </span>
                                    </div>
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-cyan-500 text-white border-transparent"
                                    >
                                      {t('scheduler.transfer', 'Transfer')}
                                    </Badge>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}
                      </div>
                    )}
                  </>
                ) : (
                  /* Multi-client mode: one row per client */
                  filteredClientRows.map(({ client, bars, transferBars }) => (
                    <div
                      key={client.id}
                      className="relative border-b"
                      style={{ height: rowHeight }}
                    >
                      {/* Delivery bars */}
                      {showDeliveries &&
                        bars.map((bar) => {
                          const style = getDeliveryBarStyle(bar);
                          const barWidth = style.width as number;
                          const statusStyle =
                            STATUS_BAR_STYLES[
                              bar.status as Exclude<
                                RequisitionStatus,
                                'Rejected'
                              >
                            ] ?? STATUS_BAR_STYLES.Requested;

                          return (
                            <TooltipProvider key={bar.id} delayDuration={150}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={cn(
                                      'absolute cursor-pointer rounded-sm border shadow-sm transition-all hover:shadow-md hover:z-10 hover:brightness-110',
                                      'flex items-center justify-center text-[10px] font-medium truncate',
                                      statusStyle.bg,
                                      statusStyle.border,
                                      statusStyle.text
                                    )}
                                    style={style}
                                  >
                                    {barWidth > 40 && (
                                      <span className="truncate px-1">
                                        {bar.suppliedQuantity > 0
                                          ? bar.suppliedQuantity
                                          : bar.requestedQuantity}
                                      </span>
                                    )}
                                    {bar.status === 'InProgress' &&
                                      bar.progress > 0 &&
                                      bar.progress < 100 && (
                                        <div
                                          className="absolute bottom-0 left-0 h-1 bg-white/40 rounded-b-sm"
                                          style={{
                                            width: `${bar.progress}%`,
                                          }}
                                        />
                                      )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-xs bg-popover text-popover-foreground border shadow-md p-3"
                                >
                                  <div className="space-y-1.5">
                                    <p className="font-semibold text-sm">
                                      {bar.folio}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {bar.clientName}
                                      {bar.locationName
                                        ? ` - ${bar.locationName}`
                                        : ''}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {format(bar.startDate, 'd MMM yyyy', {
                                        locale,
                                      })}{' '}
                                      &mdash;{' '}
                                      {format(bar.endDate, 'd MMM yyyy', {
                                        locale,
                                      })}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs">
                                      <span>
                                        {t('scheduler.requested', 'Requested')}:{' '}
                                        <strong>{bar.requestedQuantity}</strong>
                                      </span>
                                      <span>
                                        {t('scheduler.supplied', 'Supplied')}:{' '}
                                        <strong>{bar.suppliedQuantity}</strong>
                                      </span>
                                    </div>
                                    <Badge
                                      variant="outline"
                                      className={cn(
                                        'text-[10px]',
                                        statusStyle.bg,
                                        statusStyle.text,
                                        'border-transparent'
                                      )}
                                    >
                                      {t(
                                        `scheduler.status.${bar.status}`,
                                        bar.status
                                      )}
                                    </Badge>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}

                      {/* Transfer bars */}
                      {showTransfers &&
                        transferBars.map((tBar) => {
                          const style = getTransferBarStyle(tBar);
                          const barWidth = style.width as number;

                          return (
                            <TooltipProvider
                              key={`t-${tBar.id}`}
                              delayDuration={150}
                            >
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div
                                    className={cn(
                                      'absolute cursor-pointer rounded-sm border shadow-sm transition-all hover:shadow-md hover:z-10 hover:brightness-110',
                                      'flex items-center justify-center text-[10px] font-medium truncate',
                                      'bg-cyan-500 border-cyan-600 text-white'
                                    )}
                                    style={style}
                                  >
                                    {barWidth > 28 && (
                                      <span className="truncate px-1">
                                        {tBar.amount}
                                      </span>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="top"
                                  className="max-w-xs bg-popover text-popover-foreground border shadow-md p-3"
                                >
                                  <div className="space-y-1.5">
                                    <p className="font-semibold text-sm">
                                      {tBar.folio}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {tBar.clientName}
                                      {tBar.locationName
                                        ? ` - ${tBar.locationName}`
                                        : ''}
                                    </p>
                                    <p className="text-xs text-muted-foreground">
                                      {format(tBar.date, 'd MMM yyyy', {
                                        locale,
                                      })}
                                    </p>
                                    <div className="flex items-center gap-2 text-xs">
                                      <span>
                                        {t('scheduler.amount', 'Amount')}:{' '}
                                        <strong>{tBar.amount}</strong>
                                      </span>
                                    </div>
                                    <Badge
                                      variant="outline"
                                      className="text-[10px] bg-cyan-500 text-white border-transparent"
                                    >
                                      {t('scheduler.transfer', 'Transfer')}
                                    </Badge>
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          );
                        })}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer Legend */}
        <div className="flex items-center gap-4 px-4 py-2.5 border-t bg-muted/30 text-xs flex-wrap">
          {showDeliveries &&
            (
              Object.entries(STATUS_LEGEND_COLORS) as [
                Exclude<RequisitionStatus, 'Rejected'>,
                string,
              ][]
            ).map(([status, colorClass]) => (
              <div key={status} className="flex items-center gap-1.5">
                <div className={cn('w-3 h-3 rounded-sm', colorClass)} />
                <span className="text-muted-foreground">
                  {t(`scheduler.status.${status}`, status)}
                </span>
              </div>
            ))}
          {showTransfers && (
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded-sm bg-cyan-500" />
              <span className="text-muted-foreground">
                {t('scheduler.transfer', 'Transfer')}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
